import React, { Component } from 'react'
import axios from 'axios'
import classNames from 'classnames'
import Modal from '../shared/modal'
import { extractDataErrors } from '../shared/extractDataErrors'
import { PlateContainer } from './types'
import { t } from '../../i18n'

interface Props {
  expandedContainerSlot: number,
  expandedContainer: PlateContainer,
  closeContainer: () => void,
  refreshStock: () => void,
}

type AdjustmentMode = 'add' | 'remove'

interface State {
  currentView: string,
  mode: AdjustmentMode,
  amount: number,
  saving: boolean,
  saveErrorMessage: string | null
}

const scope = 'stock_control.plate_container'

class PlateContainerModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      currentView: 'edit',
      mode: 'add',
      amount: 0,
      saving: false,
      saveErrorMessage: null
    }
  }

  changeView = (value: string) => {
    this.setState({ currentView: value })
  }

  handleFormCancel = () => {
    this.props.closeContainer()
  }

  switchMode = (mode: AdjustmentMode) => {
    this.setState({ mode, amount: 0, saveErrorMessage: null })
  }

  handleAmountChange = (passedValue: string | number) => {
    const value = passedValue ? parseInt(`${passedValue}`) : 0
    if (isNaN(value)) return

    this.setState({ amount: Math.max(0, value) })
  }

  maxAmount = () => this.props.expandedContainer?.max_amount || 0

  remainingAmount = () => this.props.expandedContainer?.remaining_amount || 0

  // A preview only -- the stored total is the one the server works out.
  newTotal = () => {
    const { mode, amount } = this.state

    return mode === 'add' ? this.remainingAmount() + amount : this.remainingAmount() - amount
  }

  freeCapacity = () => Math.max(this.maxAmount() - this.remainingAmount(), 0)

  validationError = (): string | null => {
    const { mode, amount } = this.state

    if (amount <= 0) return null

    if (mode === 'add' && this.newTotal() > this.maxAmount()) {
      return t('exceeds_capacity', { scope, max: this.maxAmount(), free: this.freeCapacity() })
    }

    if (mode === 'remove' && this.newTotal() < 0) {
      return t('below_zero', { scope, remaining: this.remainingAmount() })
    }

    return null
  }

  isValid = () => !this.state.saving && this.state.amount > 0 && this.validationError() === null

  // A delta is not idempotent the way the old absolute total was, so a second
  // click would move the stand twice.
  adjustStock = () => {
    const { mode, amount, saving } = this.state
    if (saving) return

    this.setState({ saving: true })

    axios.post('/plate_containers?format=json', {
      plate_container: { slot_number: this.props.expandedContainerSlot, mode, amount }
    }).then(() => {
      this.props.refreshStock()
      this.changeView('saveConfirmation')
    }).catch(error => {
      this.setState({ saveErrorMessage: extractDataErrors(error).full_errors || null })
      this.changeView('saveError')
    }).finally(() => this.setState({ saving: false }))
  }

  modeTabs = () => {
    const { mode } = this.state

    return (
      <div className='amount-mode-tabs'>
        {(['add', 'remove'] as AdjustmentMode[]).map(tabMode =>
          <button
            key={tabMode}
            type='button'
            className={classNames('amount-mode-tab', { active: mode === tabMode })}
            onClick={() => this.switchMode(tabMode)}
          >
            {t(tabMode, { scope })}
          </button>
        )}
      </div>
    )
  }

  modalEditContent = () => {
    const { mode, amount } = this.state

    const error = this.validationError()

    const showsNewTotal = amount > 0 && !error

    return (
      <div className='container-modal edit'>
        <div className='plate-stand-status'>
          <span className='plate-stand-status-label'>{t('in_stand', { scope })}</span>
          <span className='plate-stand-status-amount'>
            <span>{this.remainingAmount()}</span>
            {showsNewTotal && <span className='plate-stand-status-preview'>{` → ${this.newTotal()}`}</span>}
            <span>{` / ${this.maxAmount()}`}</span>
          </span>
        </div>
        {this.modeTabs()}
        <div className='plate-stand-field'>
          <span className='input-label'>{t(mode === 'add' ? 'amount_to_add' : 'amount_to_remove', { scope })}</span>
          {/* No stepper here: the tabs already carry the direction, so a minus
              beside them reads as a second, contradictory way to remove bowls. */}
          <input
            className='plate-stand-amount-input'
            type='text'
            inputMode='numeric'
            value={amount}
            maxLength={3}
            onChange={({ target: { value } }) => this.handleAmountChange(value)}
          />
        </div>
        {error && <div className='plate-stand-error'>{error}</div>}
        <div className='buttons'>
          <button className='btn btn-secondary' onClick={this.handleFormCancel}>{t('cancel')}</button>
          <button className='btn btn-primary'
            onClick={this.adjustStock}
            disabled={!this.isValid()}
          >
            {t('save')}
          </button>
        </div>
      </div>
    )
  }

  modalSaveConfirmation = () => {
    return (
      <div className='container-modal save-result'>
        <div className='title-icon success mt-5'>
          <i className='fa fa-check-circle' />
        </div>
        <div className='heavy-text mt-1 mb-5'>{t('stand_updated', { scope })}</div>
        <div className='buttons'>
          <button className='btn btn-primary' onClick={this.props.closeContainer}>Continue</button>
        </div>
      </div>
    )
  }

  modalSaveError = () => {
    const { saveErrorMessage } = this.state

    return (
      <div className='container-modal save-result'>
        <div className='title-icon danger mt-5'>
          <i className='fa fa-times-circle' />
        </div>
        <div className='heavy-text mt-1 mb-5'>{saveErrorMessage || t('title', { scope: 'error_modal' })}</div>
        <div className='buttons'>
          <button className='btn btn-primary' onClick={() => this.changeView('edit')}>{t('button', { scope: 'error_modal' })}</button>
        </div>
      </div>
    )
  }

  headerText = () => {
    if (this.state.currentView !== 'edit') return null

    return t('adjust_stand', { scope, slotNumber: this.props.expandedContainer?.human_slot_number })
  }

  render() {
    const { currentView } = this.state

    const headerText = this.headerText()

    return (
      <Modal onClose={this.props.closeContainer} widen='true'
        headerContent={headerText && <h3 className='header'>{headerText}</h3>}
      >
        {currentView === 'edit' && this.modalEditContent()}
        {currentView === 'saveConfirmation' && this.modalSaveConfirmation()}
        {currentView === 'saveError' && this.modalSaveError()}
      </Modal>
    )
  }
}

export default PlateContainerModal
